import { createClient } from "@supabase/supabase-js";
import type { Database } from "@shared/types/supabase.types";

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL!;
const supabasePublishableKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY!;

export type TypedSupabaseClient = ReturnType<typeof createTypedSupabaseClient>;

function createTypedSupabaseClient() {
  return createClient<Database>(supabaseUrl, supabasePublishableKey, {
    auth: {
      // Reduce lock timeout to fail faster instead of hanging indefinitely
      storageKey: "kairo-auth",
      autoRefreshToken: true,
      persistSession: true,
      detectSessionInUrl: true,
    },
  });
}

// Use Vite's HMR data to persist client across hot reloads
if (import.meta.hot?.data) {
  if (!import.meta.hot.data.supabase) {
    import.meta.hot.data.supabase = createTypedSupabaseClient();
  }

  // Clean up on HMR disposal
  import.meta.hot.dispose(() => {
    // Note: Supabase client doesn't have a built-in dispose method,
    // but we keep the instance alive in hot.data for reuse
  });
}

export function getSupabaseClient(): TypedSupabaseClient {
  // In dev with HMR, reuse the persisted instance
  if (import.meta.hot?.data?.supabase) {
    return import.meta.hot.data.supabase;
  }

  // In production or non-HMR environments, create singleton
  if (!globalThis.__kairoSupabase) {
    globalThis.__kairoSupabase = createTypedSupabaseClient();
  }
  return globalThis.__kairoSupabase;
}

export const supabase = getSupabaseClient();

// Type augmentation for global
declare global {
  var __kairoSupabase: TypedSupabaseClient | undefined;
}
